import Cookies from 'js-cookie';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { LOGIN_REFERRER } from 'src/common/constants';
import { useFeatureIsOn } from 'src/common/growthbook';
import { type GoogleOneTapAuthResponse, useGoogleOneTapAuthMutation } from 'src/common/slices/auth';
import { apiSlice } from 'src/features/api/api-slice';

export const useGoogleOneTapAuth = () => {
  const navigate = useNavigate();
  const [googleOneTapAuth] = useGoogleOneTapAuthMutation();
  const isAdminLoginRedirectEnabled = useFeatureIsOn('b2b-admin-login-redirect');

  const navigateToDashboard = useCallback(
    (result: GoogleOneTapAuthResponse, to: string, condition: boolean) => {
      // Check if user is a company admin and redirect to company dashboard
      if (isAdminLoginRedirectEnabled && result.user.isB2BAdmin && condition) {
        const companyId = result.user.companyId;
        window.location.href = `/client/${companyId}/dashboard`;
      } else {
        // we trigger a full page reload to clear the google one tap prompt
        window.location.href = to;
      }
    },
    [isAdminLoginRedirectEnabled],
  );

  const handleAuth = useCallback(
    async (credential: string, nonce: string) => {
      const result = await googleOneTapAuth({
        credential,
        nonce,
      }).unwrap();
      if (result?.success) {
        Cookies.set('logged-in-hint', 'true', { path: '/' });

        window.sessionStorage.removeItem(LOGIN_REFERRER);

        apiSlice.util.resetApiState();

        if (result.user.isNew) {
          navigate('/onboarding');
          return;
        }

        const referrer = window.location.pathname;
        if (referrer && referrer !== '/login') {
          navigateToDashboard(result, referrer, referrer === '/');
          return;
        }

        navigateToDashboard(result, '/dashboard', true);
      }
    },
    [googleOneTapAuth, navigate, navigateToDashboard],
  );

  return handleAuth;
};
